import { FC, useState } from 'react';
import * as RestApi from 'core/network/RestApi';
import { Input, Box, Image, Checkbox, Gap } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { ImageWrapper, UrlWrapper } from '../../../../style';
import {
  useTemplateEditorDispatch,
  useTemplateEditorState,
} from 'sections/design/template-management/sections/template-editor/context/UseTemplateEditorContext';
import { useMutation } from 'react-query';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import {
  ITemplateModifyLinkPayload,
  ITemplateModifyLinkPayloadResponse,
} from 'sections/design/template-management/entities/templates';
import { IPrecompiledLink } from 'sections/design/template-management/entities/link-config';
import { generatePath } from 'react-router';

interface Props {
  link: IPrecompiledLink;
}

export const PrecompiledUrlTile: FC<Props> = ({ link }) => {
  const { t } = useTranslation('trackingLinks');
  const { linkConfigs, template } = useTemplateEditorState();
  const dispatch = useTemplateEditorDispatch();
  const [href, setHref] = useState(link.href);

  const modifyLinkMutation = useMutation((payload: ITemplateModifyLinkPayload) =>
    RestApi.post<ITemplateModifyLinkPayloadResponse, ITemplateModifyLinkPayload>(
      generatePath(TemplateApiEndpoints.MODIFY_LINK),
      payload,
    ),
  );

  function getFileURL(element) {
    return `/images/tracking_links_type/${element.kind}.svg`;
  }

  const modifyTemplate = (payload: ITemplateModifyLinkPayload) => {
    modifyLinkMutation.mutate(payload, {
      onSuccess: data => {
        dispatch({
          type: 'UPDATE_TEMPLATE_LINKS',
          payload: {
            body: data.data.precompiled,
            links: data.data.links,
          },
        });
      },
      onError: (error: RestApi.RestApiError, variables, context) => {
        console.log(error); // eslint-disable-line
      },
    });
  };

  function checkToggle(checked: boolean, configurationKind: string) {
    const linkObject = Object.assign({}, link);
    if (!linkObject.skip) {
      linkObject.skip = [];
    }
    if (linkObject) {
      if (checked) {
        // Remove skip
        const i = linkObject.skip.indexOf(configurationKind);
        if (i !== -1) {
          linkObject.skip.splice(i, 1);
        }
      } else {
        // Add skip
        if (linkObject.skip) {
          linkObject.skip.push(configurationKind);
        } else {
          link.skip.push(configurationKind);
        }
      }
    }
    const links = {};
    links[linkObject.id] = {
      skip: linkObject.skip,
    };
    const payload = {
      template: {
        type: 'body',
        value: template.template,
      },
      links,
    } as ITemplateModifyLinkPayload;
    modifyTemplate(payload);
  }

  const updateLink = () => {
    if (href && href !== link.href) {
      const payload = {
        template: { type: 'body', value: template.template },
        links: { [link.id]: { href } },
      } as ITemplateModifyLinkPayload;
      modifyTemplate(payload);
    }
  };

  function toggleSaleCycleConfiguration(checked: boolean) {
    checkToggle(checked, 'salecycle');
  }

  const salecycleisNotSkipped = !link?.skip?.includes('salecycle');

  return (
    <UrlWrapper data-testid={'LinkWrapper'}>
      <Box verticalPositioning="center">
        <div className="input-wrapper">
          <Input
            ariaLabel={t('trackingLinks:templateLevel.leftPanel.url')}
            label={t('trackingLinks:templateLevel.leftPanel.url')}
            hint={t('trackingLinks:templateLevel.leftPanel.urlHint')}
            value={href}
            onChange={e => setHref(e)}
            onBlur={updateLink}
          />
        </div>
        <Gap right={20} />
        <Box stackItems={true}>
          <ImageWrapper>
            <Image size={20} src="/images/sc-logo-midnight.svg" />
          </ImageWrapper>
          <Checkbox
            id={`salecycle${link.id}`}
            onChange={toggleSaleCycleConfiguration}
            checked={salecycleisNotSkipped}
          />
        </Box>
        <Gap right={5} />
        {linkConfigs.map(element => {
          // Toggle checkbox for tracking kind on a link
          function toggleLinkConfiguration(checked: boolean) {
            // Create object
            checkToggle(checked, element.kind);
          }

          const isNotSkipped = !link?.skip?.includes(element.kind);

          return element.isActive ? (
            <Box key={`${element.kind}.${link.id}`}>
              <Box stackItems={true}>
                <ImageWrapper>
                  <Image size={20} src={getFileURL(element)} />
                </ImageWrapper>
                <Checkbox id={`${element.kind}${link.id}`} onChange={toggleLinkConfiguration} checked={isNotSkipped} />
              </Box>
              <Gap right={5} />
            </Box>
          ) : null;
        })}
      </Box>
    </UrlWrapper>
  );
};
